import React, { useCallback } from "react";
import { ClientMutationFields } from "../api/contracts";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { ClientForm } from "../components/ClientForm";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { clientProfilePath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

export function AddClientPage({ clinicianCode }: { clinicianCode: string }) {
  const request = useCallback(
    async (signal: AbortSignal) => {
      const [formConfig, relationOptions] = await Promise.all([
        clinicianPortalApi.getRecordFormConfig(clinicianCode, signal),
        clinicianPortalApi.getRelationOptions(clinicianCode, signal),
      ]);
      return { formConfig, relationOptions };
    },
    [clinicianCode],
  );
  const state = usePortalData(request);
  const mutation = usePortalMutation();
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.formConfig.found &&
      data.formConfig.authorized &&
      data.formConfig.therapist &&
      data.relationOptions.found &&
      data.relationOptions.authorized &&
      data.relationOptions.therapist,
  );

  return (
    <ClinicianPortalLayout
      activeGlobalKey="clients"
      clinicianCode={clinicianCode}
      therapistRole={data?.formConfig.therapist?.role}
    >
      <PortalHeader
        title="Add Client"
        clinicianName={
          available ? data?.formConfig.therapist?.therapistName : undefined
        }
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "My Clients", path: "/clinician-portal/clients" },
              { label: "Add Client" },
            ]}
          />
        }
      />
      <div className="cp-page-content">
        {state.status === "loading" ? (
          <LoadingState label="Loading Client form" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Client creation is unavailable")} />
        ) : null}
        {available && data ? (
          <ClientForm
            mode="create"
            fields={data.formConfig.fields}
            relationOptions={data.relationOptions.options}
            clinicianCode={clinicianCode}
            cancelPath="/clinician-portal/clients"
            isSubmitting={mutation.state.status === "pending"}
            submissionError={
              mutation.state.status === "error" ? mutation.state.error : undefined
            }
            clearSubmissionError={mutation.reset}
            onSubmit={async (values: ClientMutationFields) => {
              if (!values.nickname || !values.clinicId) return;
              const result = await mutation.run("create-client", (signal) =>
                clinicianPortalApi.createClient(
                  clinicianCode,
                  {
                    ...values,
                    nickname: values.nickname,
                    clinicId: values.clinicId,
                  },
                  signal,
                ),
              );
              if (result?.created && result.patient) {
                navigateWithinPortal(
                  withClinicianCode(
                    clientProfilePath(result.patient.id),
                    clinicianCode,
                  ),
                );
              }
            }}
          />
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
